import { useSupabase } from '@packages/supabase'
import {
  Badge,
  Group,
  Stack,
  Table,
  Text,
  Button,
  Modal,
  Textarea,
  Select,
  ActionIcon,
  Tooltip,
  Code,
  Card
} from '@mantine/core'
import { FilterInput, H1, PageContentPaper } from '@packages/ui'
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconInfoCircle,
  IconCheck,
  IconEye,
  IconBan,
  IconRefresh
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useEffect, useMemo, useState } from 'react'

dayjs.extend(relativeTime)

interface SystemNotification {
  id: string
  created_at: string
  updated_at: string
  severity: 'info' | 'warning' | 'error' | 'critical'
  category: 'openai' | 'stripe' | 'system' | 'auth' | 'storage' | 'database'
  status: 'unread' | 'read' | 'resolved' | 'ignored'
  title: string
  message: string
  error_code?: string
  error_details?: any
  tenant_id?: string
  user_id?: string
  metadata?: any
  resolved_at?: string
  resolved_by?: string
  resolution_notes?: string
}

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'critical':
      return <IconAlertCircle size={18} color="red" />
    case 'error':
      return <IconAlertTriangle size={18} color="orange" />
    case 'warning':
      return <IconAlertTriangle size={18} color="yellow" />
    case 'info':
    default:
      return <IconInfoCircle size={18} color="blue" />
  }
}

const getSeverityBadgeColor = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'red'
    case 'error':
      return 'orange'
    case 'warning':
      return 'yellow'
    case 'info':
    default:
      return 'blue'
  }
}

const getStatusBadgeColor = (status: string) => {
  switch (status) {
    case 'unread':
      return 'red'
    case 'read':
      return 'blue'
    case 'resolved':
      return 'green'
    case 'ignored':
      return 'gray'
    default:
      return 'gray'
  }
}

export const SystemNotifications = () => {
  const { supabase } = useSupabase()
  const [notifications, setNotifications] = useState<SystemNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [filterBy, setFilterBy] = useState('')
  const [severityFilter, setSeverityFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [selectedNotification, setSelectedNotification] =
    useState<SystemNotification | null>(null)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [resolveModalOpen, setResolveModalOpen] = useState(false)
  const [resolutionNotes, setResolutionNotes] = useState('')

  const fetchNotifications = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('system_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error
      setNotifications(data || [])
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications()
  }, [])

  const filtered = useMemo(() => {
    let data = notifications

    if (filterBy) {
      data = data.filter(
        (n) =>
          n.title.toLowerCase().includes(filterBy.toLowerCase()) ||
          n.message.toLowerCase().includes(filterBy.toLowerCase()) ||
          n.error_code?.toLowerCase().includes(filterBy.toLowerCase())
      )
    }

    if (severityFilter) {
      data = data.filter((n) => n.severity === severityFilter)
    }

    if (statusFilter) {
      data = data.filter((n) => n.status === statusFilter)
    }

    if (categoryFilter) {
      data = data.filter((n) => n.category === categoryFilter)
    }

    return data
  }, [notifications, filterBy, severityFilter, statusFilter, categoryFilter])

  const updateNotificationStatus = async (
    id: string,
    status: 'read' | 'resolved' | 'ignored',
    notes?: string
  ) => {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      }

      if (status === 'resolved') {
        updateData.resolved_at = new Date().toISOString()
        updateData.resolution_notes = notes || null
      }

      const { error } = await supabase
        .from('system_notifications')
        .update(updateData)
        .eq('id', id)

      if (error) throw error

      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? {
                ...n,
                status,
                resolved_at: updateData.resolved_at,
                resolution_notes: updateData.resolution_notes
              }
            : n
        )
      )
    } catch (error) {
      console.error('Error updating notification:', error)
    }
  }

  const handleResolve = async () => {
    if (selectedNotification) {
      await updateNotificationStatus(
        selectedNotification.id,
        'resolved',
        resolutionNotes
      )
      setResolveModalOpen(false)
      setResolutionNotes('')
      setSelectedNotification(null)
    }
  }

  const showDetails = (notification: SystemNotification) => {
    setSelectedNotification(notification)
    setDetailsModalOpen(true)
  }

  const unreadCount = notifications.filter((n) => n.status === 'unread').length
  const criticalCount = notifications.filter(
    (n) => n.severity === 'critical'
  ).length

  return (
    <Stack>
      <Group justify="space-between">
        <H1 className={'text-white'}>System Notifications</H1>
        <Group>
          <Badge color="red" size="lg">
            {unreadCount} Unread
          </Badge>
          {criticalCount > 0 && (
            <Badge color="red" variant="filled" size="lg">
              {criticalCount} Critical
            </Badge>
          )}
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={fetchNotifications}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      <Group justify="space-between">
        <Group>
          <Select
            placeholder="Severity"
            data={[
              { value: '', label: 'All Severities' },
              { value: 'critical', label: 'Critical' },
              { value: 'error', label: 'Error' },
              { value: 'warning', label: 'Warning' },
              { value: 'info', label: 'Info' }
            ]}
            value={severityFilter || ''}
            onChange={(value) => setSeverityFilter(value || null)}
            clearable
          />
          <Select
            placeholder="Status"
            data={[
              { value: '', label: 'All Statuses' },
              { value: 'unread', label: 'Unread' },
              { value: 'read', label: 'Read' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'ignored', label: 'Ignored' }
            ]}
            value={statusFilter || ''}
            onChange={(value) => setStatusFilter(value || null)}
            clearable
          />
          <Select
            placeholder="Category"
            data={[
              { value: '', label: 'All Categories' },
              { value: 'openai', label: 'OpenAI' },
              { value: 'stripe', label: 'Stripe' },
              { value: 'system', label: 'System' },
              { value: 'auth', label: 'auth' },
              { value: 'storage', label: 'Storage' },
              { value: 'database', label: 'Database' }
            ]}
            value={categoryFilter || ''}
            onChange={(value) => setCategoryFilter(value || null)}
            clearable
          />
        </Group>
        <FilterInput onFilter={setFilterBy} filter={filterBy} />
      </Group>

      <PageContentPaper>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Severity</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Error Code</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center">Loading...</Text>
                </Table.Td>
              </Table.Tr>
            ) : filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center">No notifications found</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filtered.map((notification) => (
                <Table.Tr
                  key={notification.id}
                  onClick={() => showDetails(notification)}
                  className="cursor-pointer"
                  style={{
                    backgroundColor:
                      notification.status === 'unread'
                        ? 'rgba(255, 0, 0, 0.05)'
                        : undefined
                  }}
                >
                  <Table.Td>
                    <Group gap="xs">
                      {getSeverityIcon(notification.severity)}
                      <Badge
                        color={getSeverityBadgeColor(notification.severity)}
                        size="sm"
                      >
                        {notification.severity}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">
                      {notification.category}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text
                      size="sm"
                      fw={notification.status === 'unread' ? 700 : 400}
                    >
                      {notification.title}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {notification.error_code && (
                      <Code>{notification.error_code}</Code>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={getStatusBadgeColor(notification.status)}
                      size="sm"
                    >
                      {notification.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip
                      label={dayjs(notification.created_at).format(
                        'MMM D, YYYY h:mm A'
                      )}
                    >
                      <Text size="xs">
                        {dayjs(notification.created_at).fromNow()}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {notification.status === 'unread' && (
                        <Tooltip label="Mark as read">
                          <ActionIcon
                            variant="light"
                            color="blue"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              updateNotificationStatus(notification.id, 'read')
                            }}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {notification.status !== 'resolved' && (
                        <Tooltip label="Resolve">
                          <ActionIcon
                            variant="light"
                            color="green"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedNotification(notification)
                              setResolveModalOpen(true)
                            }}
                          >
                            <IconCheck size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {notification.status !== 'ignored' && (
                        <Tooltip label="Ignore">
                          <ActionIcon
                            variant="light"
                            color="gray"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              updateNotificationStatus(
                                notification.id,
                                'ignored'
                              )
                            }}
                          >
                            <IconBan size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </PageContentPaper>

      {/* Details Modal */}
      <Modal
        opened={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        title={
          <Group>
            {selectedNotification &&
              getSeverityIcon(selectedNotification.severity)}
            <Text fw={700}>{selectedNotification?.title}</Text>
          </Group>
        }
        size="lg"
      >
        {selectedNotification && (
          <Stack gap="md">
            <Group>
              <Badge
                color={getSeverityBadgeColor(selectedNotification.severity)}
              >
                {selectedNotification.severity}
              </Badge>
              <Badge variant="light">{selectedNotification.category}</Badge>
              <Badge color={getStatusBadgeColor(selectedNotification.status)}>
                {selectedNotification.status}
              </Badge>
            </Group>

            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Message:
                </Text>
                <Text size="sm">{selectedNotification.message}</Text>
              </Stack>
            </Card>

            {selectedNotification.error_code && (
              <Card withBorder>
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Error Code:
                  </Text>
                  <Code block>{selectedNotification.error_code}</Code>
                </Stack>
              </Card>
            )}

            {selectedNotification.error_details && (
              <Card withBorder>
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Error Details:
                  </Text>
                  <Code block>
                    {JSON.stringify(
                      selectedNotification.error_details,
                      null,
                      2
                    )}
                  </Code>
                </Stack>
              </Card>
            )}

            {selectedNotification.metadata &&
              Object.keys(selectedNotification.metadata).length > 0 && (
                <Card withBorder>
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Metadata:
                    </Text>
                    <Code block>
                      {JSON.stringify(selectedNotification.metadata, null, 2)}
                    </Code>
                  </Stack>
                </Card>
              )}

            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Created:{' '}
                {dayjs(selectedNotification.created_at).format(
                  'MMM D, YYYY h:mm A'
                )}
              </Text>
              {selectedNotification.resolved_at && (
                <Text size="xs" c="dimmed">
                  Resolved:{' '}
                  {dayjs(selectedNotification.resolved_at).format(
                    'MMM D, YYYY h:mm A'
                  )}
                </Text>
              )}
            </Group>

            {selectedNotification.resolution_notes && (
              <Card withBorder>
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Resolution Notes:
                  </Text>
                  <Text size="sm">{selectedNotification.resolution_notes}</Text>
                </Stack>
              </Card>
            )}
          </Stack>
        )}
      </Modal>

      {/* Resolve Modal */}
      <Modal
        opened={resolveModalOpen}
        onClose={() => {
          setResolveModalOpen(false)
          setResolutionNotes('')
        }}
        title="Resolve Notification"
      >
        <Stack>
          <Text size="sm">Add resolution notes (optional):</Text>
          <Textarea
            placeholder="Describe how this was resolved..."
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.currentTarget.value)}
            minRows={4}
          />
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => {
                setResolveModalOpen(false)
                setResolutionNotes('')
              }}
            >
              Cancel
            </Button>
            <Button color="green" onClick={handleResolve}>
              Resolve
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
